import { Permission } from '@bookora/shared';
import type { PagedEnvelope } from '@bookora/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type {
  BlacklistPatientDto,
  CreateDataExportDto,
  CreatePatientDto,
  CreatePatientNoteDto,
  CreatePatientPaymentDto,
  DataExportResponse,
  ErasurePendingCountResponse,
  ErasureRequestResponse,
  MergePatientsDto,
  MergeResultResponse,
  PackageResponse,
  PatientDetailResponse,
  PatientFacetsResponse,
  PatientNoteResponse,
  PatientPaymentResponse,
  PatientRegistrationLimitResponse,
  PatientResponse,
  PatientSummaryResponse,
  PatientsListParams,
  SessionResponse,
  StaffResponse,
  UpdatePatientDto,
  UpdatePatientNoteDto,
} from '@/api/generated/model';
import { useAuth } from '@/auth/token-store';
import { useCan } from '@/permissions/use-can';

import {
  approveErasure,
  completeReminder,
  createPatient,
  createPatientNote,
  deletePatient,
  deletePatientNote,
  getErasurePendingCount,
  getPatient,
  getPatientFacets,
  getPatientOverview,
  getRegistrationLimit,
  listDeletedPatients,
  listDoctors,
  listErasureRequests,
  listPatientExports,
  listPatientNotes,
  listPatientPayments,
  listPatients,
  listPatientSessions,
  listPatientTags,
  listSellablePackages,
  mergePatient,
  recordPatientPayment,
  rejectErasure,
  requestErasure,
  requestPatientExport,
  restorePatient,
  setPatientBlacklist,
  updatePatient,
  updatePatientNote,
} from './api';
import {
  DEFAULT_PATIENT_FILTERS,
  DORMANT_MONTHS,
  patientKeys,
  type PatientListFilters,
} from './keys';
import type { PatientUpdate } from './patient-form-values';

/**
 * The patient module's data layer.
 *
 * Every read is a `useQuery` over a generated client (see `api.ts`) with an
 * `enabled` gate that mirrors the endpoint's own permission, so a caller who
 * cannot see a thing never issues the request — no wasted round trip, and no
 * red 403 in the console that looks like a bug during a demo. This is the same
 * pattern the dashboard uses; only the queries differ.
 *
 * Mutations invalidate `patientKeys.all(clinicId)` rather than a surgical key.
 * A patient edit moves the row in the list, changes the detail, may change the
 * financial summary and can alter which page it lands on; enumerating that is
 * how a screen ends up showing a name that no longer exists.
 */

/** Turns the screen's filter state into the query the API actually accepts. */
export function toListParams(
  filters: PatientListFilters,
  page: number,
  pageSize: number,
): PatientsListParams {
  const q = filters.q.trim();
  return {
    page,
    pageSize,
    ...(q === '' ? null : { q }),
    ...(filters.assignedDoctorId === null ? null : { assignedDoctorId: filters.assignedDoctorId }),
    ...(filters.gender === null
      ? null
      : { gender: filters.gender as NonNullable<PatientsListParams['gender']> }),
    ...(filters.skinType === null
      ? null
      : { skinType: filters.skinType as NonNullable<PatientsListParams['skinType']> }),
    ...(filters.acquisitionSource === null
      ? null
      : {
          acquisitionSource: filters.acquisitionSource as NonNullable<
            PatientsListParams['acquisitionSource']
          >,
        }),
    ...(filters.tag === null ? null : { tags: [filters.tag] }),
    ...(filters.city === null ? null : { city: filters.city }),
    ...(filters.owes ? { hasOutstandingBalance: true } : null),
    ...flagParams(filters.flag),
    sort: filters.sort,
    direction: filters.direction,
  };
}

function flagParams(flag: PatientListFilters['flag']): PatientsListParams {
  switch (flag) {
    case 'blacklisted':
      return { isBlacklisted: true };
    case 'smsOptOut':
      return { smsOptOut: true };
    case 'unassigned':
      return { unassigned: true };
    case 'hasUpcoming':
      return { hasUpcomingAppointment: true };
    case 'neverVisited':
      return { neverVisited: true };
    case 'dormant': {
      // «Not seen in six months» is a recall list: a last visit ON OR BEFORE
      // the cut-off. Never-visited files have no last visit and are not in it —
      // they have their own flag.
      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - DORMANT_MONTHS);
      return { lastVisitTo: cutoff.toISOString() };
    }
    default:
      return {};
  }
}

/* -------------------------------------------------------------------------- */
/* Reads                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The money-backed parts of the list query — «فقط بدهکارها» and the two money
 * sorts — for a caller who does not hold `finance.view_revenue`, dropped here
 * rather than sent. The server answers them with a 403, and the only way such
 * a filter reaches this screen is a URL shared by a colleague who does hold
 * the key: that link should open the list, not an error.
 */
export function withoutMoneyFilters(
  filters: PatientListFilters,
  canViewMoney: boolean,
): PatientListFilters {
  if (canViewMoney) return filters;
  const moneySort =
    filters.sort === 'outstandingBalanceRials' || filters.sort === 'lifetimeValueRials';
  if (!filters.owes && !moneySort) return filters;
  return {
    ...filters,
    owes: false,
    ...(moneySort
      ? { sort: DEFAULT_PATIENT_FILTERS.sort, direction: DEFAULT_PATIENT_FILTERS.direction }
      : null),
  };
}

export function usePatientList(
  requested: PatientListFilters,
  page: number,
  pageSize: number,
): UseQueryResult<PagedEnvelope<PatientSummaryResponse>> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);
  const canViewMoney = useCan(Permission.FINANCE_VIEW_REVENUE);
  const filters = withoutMoneyFilters(requested, canViewMoney);

  return useQuery({
    queryKey: patientKeys.list(activeClinicId, filters, page, pageSize),
    queryFn: ({ signal }) => listPatients(toListParams(filters, page, pageSize), signal),
    enabled: canView && Boolean(activeClinicId),
    // A patient list changes when the desk registers someone, which is often
    // enough that a stale minute is fine and a refetch per keystroke is not.
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * The recycle bin. Gated on BOTH keys because the endpoint is: reading deleted
 * files needs `patient.view` to see patients at all and `patient.delete` to have
 * any business looking at what was removed.
 */
export function useDeletedPatients(
  q: string,
  page: number,
  pageSize: number,
): UseQueryResult<PagedEnvelope<PatientSummaryResponse>> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);
  const canDelete = useCan(Permission.PATIENT_DELETE);
  const trimmed = q.trim();

  return useQuery({
    queryKey: patientKeys.recycleBin(activeClinicId, trimmed, page, pageSize),
    queryFn: ({ signal }) =>
      listDeletedPatients({ page, pageSize, ...(trimmed === '' ? null : { q: trimmed }) }, signal),
    enabled: canView && canDelete && Boolean(activeClinicId),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

/** The bare record — what the edit form loads. */
export function usePatient(id: string | undefined): UseQueryResult<PatientResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);

  return useQuery({
    queryKey: patientKeys.detail(activeClinicId, id ?? ''),
    queryFn: ({ signal }) => getPatient(id as string, signal),
    enabled: canView && Boolean(id) && Boolean(activeClinicId),
  });
}

/**
 * The whole file in one request: identity, appointments, sessions, consents,
 * packages, follow-ups, photo count and the financial summary.
 *
 * Sections the caller may not see come back as `null` rather than `[]`, which is
 * the distinction the detail screen needs in order to say «دسترسی ندارید»
 * instead of «چیزی ثبت نشده». Do not collapse a null into an empty array
 * anywhere downstream.
 */
export function usePatientOverview(id: string | undefined): UseQueryResult<PatientDetailResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);

  return useQuery({
    queryKey: patientKeys.overview(activeClinicId, id ?? ''),
    queryFn: ({ signal }) => getPatientOverview(id as string, signal),
    enabled: canView && Boolean(id) && Boolean(activeClinicId),
  });
}

export function usePatientNotes(
  patientId: string | undefined,
  page: number,
  pageSize = 20,
): UseQueryResult<PagedEnvelope<PatientNoteResponse>> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);

  return useQuery({
    queryKey: patientKeys.notes(activeClinicId, patientId ?? '', page),
    queryFn: ({ signal }) => listPatientNotes(patientId as string, { page, pageSize }, signal),
    enabled: canView && Boolean(patientId) && Boolean(activeClinicId),
  });
}

/**
 * The patient's recorded receipts and refunds.
 *
 * Gated on `finance.view_revenue` — the same key that decides whether the
 * overview's `financial` block comes back at all, so a caller who sees the
 * summary sees the rows behind it and one who does not sees neither.
 */
export function usePatientPayments(
  patientId: string | undefined,
  pageSize = 10,
): UseQueryResult<PagedEnvelope<PatientPaymentResponse>> {
  const { activeClinicId } = useAuth();
  const canViewMoney = useCan(Permission.FINANCE_VIEW_REVENUE);

  return useQuery({
    queryKey: patientKeys.payments(activeClinicId, patientId ?? '', pageSize),
    placeholderData: (previous) => previous,
    queryFn: ({ signal }) => listPatientPayments(patientId as string, pageSize, signal),
    enabled: canViewMoney && Boolean(patientId) && Boolean(activeClinicId),
  });
}

/**
 * The clinic's doctors, for the «پزشک معالج» filter and form field.
 *
 * Needs `staff.view`. A user without it simply gets no doctor control — the
 * filter is an affordance, and the list they see is already narrowed by the
 * server to whatever `patient.view_all` allows.
 */
export function useDoctors(): UseQueryResult<StaffResponse[]> {
  const { activeClinicId } = useAuth();
  const canViewStaff = useCan(Permission.STAFF_VIEW);

  return useQuery({
    queryKey: patientKeys.doctors(activeClinicId),
    queryFn: ({ signal }) => listDoctors(signal),
    enabled: canViewStaff && Boolean(activeClinicId),
    // The roster changes a few times a year.
    staleTime: 10 * 60_000,
  });
}

/**
 * Tags already in use at this clinic, most common first — so the form offers
 * «وی‌آی‌پی» before anyone types «VIP» next to it. `GET /patients/tags` counts
 * the whole (doctor-scoped) vocabulary; this used to be a sample harvested from
 * the 100 most recent files. The field still accepts a new tag.
 */
export function useKnownTags(): UseQueryResult<string[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);

  return useQuery({
    queryKey: patientKeys.tags(activeClinicId),
    queryFn: async ({ signal }) => (await listPatientTags(signal)).map((row) => row.value),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 5 * 60_000,
  });
}

/**
 * The cities and tags in use, with counts — what the list's «شهر» and
 * «برچسب» pickers offer before a single letter is typed.
 */
export function usePatientFacets(): UseQueryResult<PatientFacetsResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);

  return useQuery({
    queryKey: patientKeys.facets(activeClinicId),
    queryFn: ({ signal }) => getPatientFacets(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 5 * 60_000,
  });
}

/**
 * The plan's room for new files. `patient.create` — the only people who need
 * the answer are the ones who could press «ثبت بیمار جدید». Invalidated with the
 * rest of the module, so a registration or a delete moves the count.
 */
export function useRegistrationLimit(): UseQueryResult<PatientRegistrationLimitResponse> {
  const { activeClinicId } = useAuth();
  const canCreate = useCan(Permission.PATIENT_CREATE);

  return useQuery({
    queryKey: patientKeys.registrationLimit(activeClinicId),
    queryFn: ({ signal }) => getRegistrationLimit(signal),
    enabled: canCreate && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/**
 * The data packages already requested for one patient, newest first.
 *
 * Polls every three seconds while one is still being built, and stops on its
 * own the moment none is — a package takes seconds to minutes, and the modal
 * that shows it should turn into «دانلود» without anyone pressing refresh.
 */
export function usePatientExports(
  patientId: string,
  enabled: boolean,
): UseQueryResult<PagedEnvelope<DataExportResponse>> {
  const { activeClinicId } = useAuth();
  const canExport = useCan(Permission.PATIENT_EXPORT);

  return useQuery({
    queryKey: patientKeys.exports(activeClinicId, patientId),
    queryFn: ({ signal }) => listPatientExports(patientId, signal),
    enabled: enabled && canExport && Boolean(activeClinicId),
    refetchInterval: (query) =>
      query.state.data?.items.some((row) => row.status === 'PENDING' || row.status === 'PROCESSING')
        ? 3_000
        : false,
  });
}

export function useErasureRequests(
  page: number,
  enabled = true,
): UseQueryResult<PagedEnvelope<ErasureRequestResponse>> {
  const { activeClinicId } = useAuth();
  const canDelete = useCan(Permission.PATIENT_DELETE);

  return useQuery({
    queryKey: patientKeys.erasureRequests(activeClinicId, page),
    queryFn: ({ signal }) => listErasureRequests({ page, pageSize: 20 }, signal),
    enabled: enabled && canDelete && Boolean(activeClinicId),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * How many erasure requests wait for a decision — the count on the recycle
 * bin's «درخواست‌های حذف دائمی» tab. It used to be counted off page 1 of the
 * list, so the 21st pending request was invisible.
 */
export function useErasurePendingCount(): UseQueryResult<ErasurePendingCountResponse> {
  const { activeClinicId } = useAuth();
  const canDelete = useCan(Permission.PATIENT_DELETE);

  return useQuery({
    queryKey: patientKeys.erasurePendingCount(activeClinicId),
    queryFn: ({ signal }) => getErasurePendingCount(signal),
    enabled: canDelete && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

/** The patient's sessions with their balances, for the receipt modal. */
export function usePatientSessionBalances(
  patientId: string,
  enabled: boolean,
): UseQueryResult<SessionResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SESSION_VIEW);

  return useQuery({
    queryKey: [...patientKeys.all(activeClinicId), 'session-balances', patientId] as const,
    queryFn: ({ signal }) => listPatientSessions(patientId, signal),
    enabled: enabled && canView && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

/** Active offers the desk may sell from the file. `package.view` to list them. */
export function useSellablePackages(enabled: boolean): UseQueryResult<PackageResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PACKAGE_VIEW);

  return useQuery({
    queryKey: patientKeys.sellablePackages(activeClinicId),
    queryFn: ({ signal }) => listSellablePackages(signal),
    enabled: enabled && canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                     */
/* -------------------------------------------------------------------------- */

function useInvalidatePatients(): () => void {
  const client = useQueryClient();
  const { activeClinicId } = useAuth();
  return () => {
    void client.invalidateQueries({ queryKey: patientKeys.all(activeClinicId) });
  };
}

export function useCreatePatient(): UseMutationResult<PatientResponse, unknown, CreatePatientDto> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (dto: CreatePatientDto) => createPatient(dto),
    onSuccess: invalidate,
  });
}

/**
 * `PatientUpdate` rather than `UpdatePatientDto`: a cleared optional field is
 * sent as `null`, which the endpoint accepts and the generated type does not
 * describe. See the header of `toUpdateDto` in `patient-form-values.ts` for why
 * that gap exists and what would close it.
 */
export function useUpdatePatient(
  id: string,
): UseMutationResult<PatientResponse, unknown, PatientUpdate> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (dto: PatientUpdate) => updatePatient(id, dto as UpdatePatientDto),
    onSuccess: invalidate,
  });
}

export function useDeletePatient(): UseMutationResult<void, unknown, string> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (id: string) => deletePatient(id),
    onSuccess: invalidate,
  });
}

export function useRestorePatient(): UseMutationResult<PatientResponse, unknown, string> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (id: string) => restorePatient(id),
    onSuccess: invalidate,
  });
}

export function useSetBlacklist(
  id: string,
): UseMutationResult<PatientResponse, unknown, BlacklistPatientDto> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (dto: BlacklistPatientDto) => setPatientBlacklist(id, dto),
    onSuccess: invalidate,
  });
}

export function useCreateNote(
  patientId: string,
): UseMutationResult<PatientNoteResponse, unknown, CreatePatientNoteDto> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (dto: CreatePatientNoteDto) => createPatientNote(patientId, dto),
    onSuccess: invalidate,
  });
}

export function useUpdateNote(
  patientId: string,
): UseMutationResult<PatientNoteResponse, unknown, { noteId: string; dto: UpdatePatientNoteDto }> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: ({ noteId, dto }: { noteId: string; dto: UpdatePatientNoteDto }) =>
      updatePatientNote(patientId, noteId, dto),
    onSuccess: invalidate,
  });
}

export function useDeleteNote(patientId: string): UseMutationResult<void, unknown, string> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (noteId: string) => deletePatientNote(patientId, noteId),
    onSuccess: invalidate,
  });
}

/**
 * Merges a duplicate INTO `targetId`. Invalidates the whole module: the source
 * leaves the list, the target's counts and timeline change, and the recycle bin
 * gains a row.
 */
export function useMergePatient(
  targetId: string,
): UseMutationResult<MergeResultResponse, unknown, MergePatientsDto> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (dto: MergePatientsDto) => mergePatient(targetId, dto),
    onSuccess: invalidate,
  });
}

export function useRequestExport(): UseMutationResult<
  DataExportResponse,
  unknown,
  CreateDataExportDto
> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (dto: CreateDataExportDto) => requestPatientExport(dto),
    onSuccess: invalidate,
  });
}

export function useRequestErasure(): UseMutationResult<
  ErasureRequestResponse,
  unknown,
  { patientId: string; reason: string }
> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: ({ patientId, reason }: { patientId: string; reason: string }) =>
      requestErasure({ patientId, reason }),
    onSuccess: invalidate,
  });
}

export function useApproveErasure(): UseMutationResult<ErasureRequestResponse, unknown, string> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (id: string) => approveErasure(id),
    onSuccess: invalidate,
  });
}

export function useRejectErasure(): UseMutationResult<
  ErasureRequestResponse,
  unknown,
  { id: string; reason: string }
> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectErasure(id, reason),
    onSuccess: invalidate,
  });
}

/**
 * «ثبت دریافت» from the file. Invalidates the accounting section's cache too —
 * the same receipt is a row in its ledger and a figure in its summaries — and
 * the sessions section's, whose rows carry each session's `balanceRials`.
 * Without that a second receipt opened within the minute would offer the old
 * balance again, and one click would record the same debt twice.
 */
export function useRecordPayment(): UseMutationResult<unknown, unknown, CreatePatientPaymentDto> {
  const invalidate = useInvalidatePatients();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePatientPaymentDto) => recordPatientPayment(dto),
    onSuccess: () => {
      invalidate();
      void client.invalidateQueries({ queryKey: ['accounting'] });
      void client.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

/**
 * ── A WRITE MADE FROM THE FILE MUST SHOW IN THE FILE ────────────────────────
 *
 * «ثبت جلسه» and «فروش بسته خدمات» open the SESSIONS and PACKAGES sections' own
 * drawers, whose mutations refresh those sections' caches and not this one.
 * The file therefore kept saying «جلسه‌ای ثبت نشده», «مجموع صورت‌حساب ۰
 * تومان», «بسته خدماتی خریداری نشده» and «هنوز دریافتی ثبت نشده» under the
 * toast that said the opposite — and a receptionist who believes the save
 * failed records it again. The receipt modal was right only because it is this
 * module's own mutation (`useRecordPayment`).
 *
 * The file calls this from each drawer's success callback. `patientKeys.all`
 * covers the overview (identity dates, timeline, packages, follow-ups and the
 * money summary all ride on it), the receipts list and the session balances;
 * accounting and sessions follow for the same reason `useRecordPayment` gives
 * — a package sold with its first payment is a receipt, and a session is a
 * balance the next «ثبت دریافت» will offer.
 */
export function useRefreshPatientFile(): () => void {
  const invalidate = useInvalidatePatients();
  const client = useQueryClient();
  return () => {
    invalidate();
    void client.invalidateQueries({ queryKey: ['accounting'] });
    void client.invalidateQueries({ queryKey: ['sessions'] });
  };
}

export function useCompleteReminder(): UseMutationResult<unknown, unknown, string> {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: (id: string) => completeReminder(id, {}),
    onSuccess: invalidate,
  });
}
